import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, CheckCheck, Hourglass, ImagePlus, Mic, Send, Square, Star, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = { online: "Online", reading: "Reading", away: "Away", offline: "Offline" };
const STATUS_DOT = { online: "bg-emerald-300", reading: "bg-[#c77dff]", away: "bg-amber-300", offline: "bg-slate-500" };

const fmtTime = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
};

const fmtClock = (s) =>
    `${String(Math.floor(Math.max(0, s) / 60)).padStart(2, "0")}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;

const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });

const Bubble = ({ m }) => {
    const mine = m.sender === "customer";
    return (
        <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35 }}
            data-testid={`chat-message-${m.id}`}
            className={`flex ${mine ? "justify-end" : "justify-start"}`}
        >
            <div
                className={`max-w-[82%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed backdrop-blur-xl ${
                    mine
                        ? "rounded-br-sm border border-[#c77dff]/40 bg-[#5a189a]/50 text-[#f8f7ff] shadow-[0_0_20px_rgba(123,44,191,0.35)]"
                        : "rounded-bl-sm border border-silver/25 bg-[#0b031e]/70 text-[#f8f7ff]/90"
                }`}
            >
                {m.kind === "image" ? (
                    <img src={m.body} alt="A glimpse sent through the mirror" data-testid={`chat-image-${m.id}`} className="max-h-64 rounded-xl" />
                ) : m.kind === "voice" ? (
                    <div>
                        <audio controls src={m.body} data-testid={`chat-voice-${m.id}`} className="w-56" />
                        {m.meta?.transcript && (
                            <p
                                data-testid={`chat-voice-transcript-${m.id}`}
                                className="mt-2 border-t border-[#c77dff]/20 pt-2 font-cormorant text-sm italic text-[#e0aaff]/85"
                            >
                                "{m.meta.transcript}"
                            </p>
                        )}
                    </div>
                ) : (
                    <p>{m.body}</p>
                )}
                <div className="mt-1.5 flex items-center justify-end gap-1.5 text-[10px] tracking-wider text-[#c77dff]/60">
                    <span>{fmtTime(m.created_at)}</span>
                    {mine &&
                        (m.status === "read" ? (
                            <CheckCheck data-testid="msg-read" className="h-3 w-3 text-[#e0aaff]" />
                        ) : (
                            <Check data-testid="msg-delivered" className="h-3 w-3" />
                        ))}
                </div>
            </div>
        </motion.div>
    );
};

const RecognitionCard = ({ m, onContinue }) => {
    const meta = m.meta || {};
    const result = meta.result || "unclear";
    const headings = {
        yes: "THE MIRROR RECOGNISES HER",
        no: "THE MIRROR DOES NOT RECOGNISE THIS CONNECTION",
        unclear: "THE MIRROR HAS NOT FULLY OPENED",
    };
    const copies = {
        yes: "Mara has confirmed the first pattern: this woman may be your Lunar Mirror Sister, and something meant for your path has crossed into hers. What follows is what the mirror showed — read it slowly.",
        no: "The mirror gives no false alarms: this does not appear to be a Dichotomy Sister connection. Sometimes a painful or unfair experience is exactly that — painful and unfair — but not the Blessing Reversal.",
        unclear: "The mirror has not fully opened: there are similarities within these two paths, but not enough to confirm a Lunar Mirror Sister. Mara will tell you honestly when the pattern is not there.",
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.92, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`recognition-card-${result}`}
            className="glass-card relative overflow-hidden border-[#c77dff]/40 p-7 shadow-[0_0_50px_rgba(123,44,191,0.4)] sm:p-9"
        >
            <div className="mirror-sheen pointer-events-none absolute inset-0 opacity-30" />
            <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 opacity-20">
                <img src="/assets/sisters-mark.png" alt="" aria-hidden="true" className="h-full w-full rounded-full object-cover" />
            </div>
            <p className="eyebrow mb-3">The Mirror Warns — Mara's recognition</p>
            <h3 className="text-glow-lilac font-display text-lg font-bold tracking-wide text-[#f8f7ff] sm:text-xl">
                {headings[result]}
            </h3>
            <p className="mt-4 font-cormorant text-base italic leading-relaxed text-[#e0aaff]/85">{copies[result]}</p>

            {result === "yes" && (
                <dl className="mt-6 grid gap-4 sm:grid-cols-2">
                    {[
                        ["Possible Dichotomy Sister", meta.possible_sister],
                        ["Relationship to you", meta.relationship],
                        ["Blessing involved", meta.area],
                        ["Where the paths reversed", meta.diverted],
                    ].map(([k, v]) => (
                        <div key={k} className="rounded-xl border border-purple-500/25 bg-[#0b031e]/60 p-4">
                            <dt className="eyebrow">{k}</dt>
                            <dd className="mt-2 text-sm leading-relaxed text-[#f8f7ff]/90">{v}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {meta.explanation && (
                <p className="mt-5 text-sm leading-relaxed text-[#f8f7ff]/80">{meta.explanation}</p>
            )}
            {meta.next_step && result !== "no" && (
                <p className="mt-3 text-sm italic text-[#e0aaff]/70">{meta.next_step}</p>
            )}

            {result === "yes" && (
                <button type="button" onClick={onContinue} data-testid="continue-live-button" className="btn-glow relative mt-7 w-full sm:w-auto">
                    <span className="mirror-sheen absolute inset-0 animate-shimmer-line" />
                    <span className="relative">CONTINUE LIVE WITH MARA</span>
                </button>
            )}
        </motion.div>
    );
};

const TIME_TIERS = [
    { id: "reading_30", minutes: 30, usd: 120 },
    { id: "reading_45", minutes: 45, usd: 180 },
    { id: "reading_60", minutes: 60, usd: 240 },
];

const RequestTimePanel = ({ balance, requesting, onRequest }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="topup-panel"
        className="glass-card border-[#c77dff]/30 p-6"
    >
        <p className="eyebrow mb-2">Request more mirror time</p>
        <p className="font-cormorant text-sm italic leading-relaxed text-[#e0aaff]/80">
            Readings begin at $120 for a half hour — $4 per minute · nothing is charged until Mara accepts your
            request · current balance {fmtClock(balance)}
        </p>
        <div className="mt-4 grid grid-cols-3 gap-3">
            {TIME_TIERS.map((t) => (
                <button
                    key={t.id}
                    type="button"
                    disabled={!!requesting}
                    onClick={() => onRequest(t)}
                    data-testid={`request-time-${t.minutes}`}
                    className="rounded-xl border border-purple-500/30 bg-[#0b031e]/60 px-3 py-4 text-center transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-[#c77dff]/60 hover:shadow-[0_0_20px_rgba(199,125,255,0.3)] disabled:opacity-60"
                >
                    <span className="block font-display text-xl font-bold text-[#e0aaff]">{t.minutes}</span>
                    <span className="block font-cinzel text-[10px] tracking-[0.2em] text-[#c77dff]/70">MINUTES</span>
                    <span className="mt-1 block text-sm text-[#f8f7ff]">${t.usd}</span>
                </button>
            ))}
        </div>
        {requesting && <p className="mt-3 text-center text-xs tracking-wider text-[#c77dff]/70">Carrying your request to Mara…</p>}
    </motion.div>
);

const RatingPanel = ({ conversationId, onDone }) => {
    const [stars, setStars] = useState(0);
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async () => {
        if (!stars || sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/recognition/conversations/${conversationId}/rating`, { stars, note: note || null });
            toast.success("Thank you — your words stay between you, Mara and the mirror.");
            onDone();
        } catch {
            toast.error("The mirror kept that to itself. Try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="rating-panel"
            className="glass-card border-[#c77dff]/30 p-6 text-center"
        >
            <p className="eyebrow mb-2">The mirror has closed</p>
            <p className="font-cormorant text-base italic text-[#e0aaff]/80">
                How was your crossing? Only Mara will see this.
            </p>
            <div className="mt-4 flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setStars(s)}
                        data-testid={`rating-star-${s}`}
                        aria-label={`${s} stars`}
                        className="transition-transform duration-200 hover:scale-125 active:scale-95"
                    >
                        <Star className={`h-7 w-7 ${s <= stars ? "fill-[#ffd700]/80 text-[#ffd700]" : "text-[#c77dff]/40"}`} />
                    </button>
                ))}
            </div>
            <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="rating-note-input"
                placeholder="A private word for Mara… (optional)"
                className="mt-4 w-full rounded-full border border-purple-500/30 bg-[#0b031e]/70 px-5 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 transition-colors duration-300 focus:border-[#c77dff]/70 focus:outline-none"
            />
            <button type="button" onClick={submit} disabled={!stars || sending} data-testid="rating-submit-button" className="btn-ghost-glow mt-4 disabled:opacity-50">
                LEAVE YOUR RATING
            </button>
        </motion.div>
    );
};

export const BackRoom = ({ conversationId, onClose }) => {
    const [data, setData] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [flash, setFlash] = useState(false);
    const [showTopUp, setShowTopUp] = useState(false);
    const [requesting, setRequesting] = useState(null);
    const [displayRemaining, setDisplayRemaining] = useState(null);
    const [recording, setRecording] = useState(false);
    const recorderRef = useRef(null);
    const flashedRef = useRef(false);
    const warnedRef = useRef(new Set());
    const bottomRef = useRef(null);
    const photoRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/recognition/conversations/${conversationId}`);
            setData(res.data);
        } catch {
            onClose(true);
        }
    }, [conversationId, onClose]);

    useEffect(() => {
        load();
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [load]);

    const messages = data?.messages || [];
    const recognition = messages.find((m) => m.kind === "recognition");
    const customerCount = messages.filter((m) => m.sender === "customer").length;
    const gazing = !recognition && customerCount > 0;
    const presence = data?.presence || { status: "online", eta: "" };
    const mirror = data?.mirror_time || { state: "idle", remaining_seconds: 0 };
    const paidPhase = !!recognition;
    const remaining = displayRemaining ?? mirror.remaining_seconds ?? 0;
    const blocked = !!data?.conversation?.blocked;
    const lockedInput =
        blocked || (paidPhase && remaining <= 0 && mirror.state !== "running" && !promoAvailable);
    const mirrorClosing = mirror.state === "running" && remaining > 0 && remaining <= 120;
    const mirrorClosed =
        paidPhase &&
        (mirror.state === "time_expired" || mirror.state === "ended" || (remaining <= 0 && mirror.state !== "running"));
    const hasPhoto = messages.some((m) => m.sender === "customer" && m.kind === "image");
    const hasVoice = messages.some((m) => m.sender === "customer" && m.kind === "voice");
    const promo = data?.promo;
    const promoAvailable = !!promo?.active && !promo?.claimed;

    const claimPromo = async () => {
        try {
            await axios.post(`${API}/promo/claim`, { conversation_id: conversationId });
            toast.success("Five minutes gifted by the mirror.");
            await load();
        } catch {
            toast.error("The gift was already taken — or the free hours have passed.");
        }
    };

    useEffect(() => {
        setDisplayRemaining(mirror.remaining_seconds);
    }, [mirror.remaining_seconds, mirror.state]);

    useEffect(() => {
        if (mirror.state !== "running") return;
        const t = setInterval(() => setDisplayRemaining((r) => (r && r > 0 ? r - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, [mirror.state]);

    useEffect(() => {
        if (mirror.state !== "running" || remaining == null) return;
        [
            [300, "Five minutes of mirror time remain."],
            [120, null],
            [30, "Thirty seconds — the mirror dims."],
        ].forEach(([th, msg]) => {
            if (remaining <= th && !warnedRef.current.has(th)) {
                warnedRef.current.add(th);
                if (th === 120) setShowTopUp(true);
                if (msg) toast.info(msg);
            }
        });
    }, [remaining, mirror.state]);

    useEffect(() => {
        if (recognition?.meta?.result === "yes" && !flashedRef.current) {
            flashedRef.current = true;
            setFlash(true);
            const t = setTimeout(() => setFlash(false), 2600);
            return () => clearTimeout(t);
        }
    }, [recognition]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length, gazing, showTopUp]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/recognition/conversations/${conversationId}/messages`, { body: text.trim() });
            setText("");
            await load();
        } catch {
            toast.error("The mirror clouded. Try again.");
        } finally {
            setSending(false);
        }
    };

    const sendAttachment = async (kind, body) => {
        try {
            await axios.post(`${API}/recognition/conversations/${conversationId}/messages`, { body, kind });
            await load();
        } catch {
            toast.error("The mirror refused it. Try something smaller.");
        }
    };

    const onPhoto = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 2_000_000) {
            toast.error("The photograph is too heavy for the mirror — 2MB is the limit.");
            return;
        }
        await sendAttachment("image", await fileToDataUrl(f));
    };

    const toggleRecording = async () => {
        if (recording) {
            recorderRef.current?.stop();
            setRecording(false);
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            const chunks = [];
            rec.ondataavailable = (ev) => chunks.push(ev.data);
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
                if (blob.size > 2_500_000) {
                    toast.error("That whisper was too long for the mirror to carry.");
                    return;
                }
                await sendAttachment("voice", await fileToDataUrl(blob));
            };
            rec.start();
            recorderRef.current = rec;
            setRecording(true);
        } catch {
            toast.error("The mirror could not hear you — microphone permission was refused.");
        }
    };

    const requestTime = async (tier) => {
        setRequesting(tier.id);
        try {
            await axios.post(`${API}/bookings`, {
                reading: tier.id,
                conversation_id: conversationId,
                intention: "More time requested from inside the Back Room.",
            });
            toast.success("Your request is with Mara — nothing is charged until she accepts.");
            setShowTopUp(false);
        } catch {
            toast.error("The mirror clouded. Try again.");
        } finally {
            setRequesting(null);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            data-testid="back-room-overlay"
            className="fixed inset-0 z-[90] bg-[#06010f]/95 backdrop-blur-2xl"
        >
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[36rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 opacity-[0.05]">
                <img src="/assets/sisters-logo.png" alt="" aria-hidden="true" className="h-full w-full rounded-full object-cover" />
            </div>
            <div className="pointer-events-none absolute inset-0">
                {[...Array(14)].map((_, i) => (
                    <span
                        key={i}
                        className="animate-pulse-soft absolute h-1 w-1 rounded-full bg-[#e0aaff]/60"
                        style={{ left: `${(i * 71) % 100}%`, top: `${(i * 37) % 100}%`, animationDelay: `${i * 0.6}s` }}
                    />
                ))}
            </div>

            <div className="relative mx-auto flex h-full max-w-3xl flex-col px-4 sm:px-6">
                <div className="flex items-center justify-between gap-4 border-b border-purple-500/20 py-4">
                    <div className="flex items-center gap-3">
                        <span className="relative h-11 w-11 overflow-hidden rounded-full ring-1 ring-silver/40">
                            <img src="/assets/mara-photo.jpg" alt="Mara Vey" className="h-full w-full object-cover object-top" />
                            <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#06010f] ${STATUS_DOT[presence.status] || STATUS_DOT.online}`} />
                        </span>
                        <div>
                            <p className="font-cinzel text-sm tracking-[0.25em] text-[#f8f7ff]">MARA'S BACK ROOM</p>
                            <p data-testid="mara-status" className="mt-0.5 text-[11px] tracking-wider text-[#c77dff]/70">
                                Mara Vey · {STATUS_LABEL[presence.status] || "Online"}
                                {presence.eta ? ` · ${presence.eta}` : ""}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {data?.customer?.timezone && (
                            <span data-testid="customer-timezone" className="hidden rounded-full border border-purple-500/25 px-3 py-1.5 text-[10px] tracking-[0.15em] text-[#e0aaff]/60 sm:block">
                                {data.customer.timezone}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => onClose(false)}
                            data-testid="back-room-close"
                            aria-label="Leave the Back Room"
                            className="rounded-full border border-purple-500/30 p-2 text-[#e0aaff]/70 transition-colors duration-300 hover:text-[#f8f7ff]"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {(paidPhase || remaining > 0) && (
                    <div data-testid="mirror-time-bar" className="flex items-center justify-between gap-3 border-b border-purple-500/20 py-2.5">
                        <p className="flex items-center gap-2 text-[11px] tracking-[0.2em] text-[#e0aaff]/70">
                            <Hourglass className="h-3.5 w-3.5" />
                            MIRROR TIME REMAINING:{" "}
                            <span data-testid="mirror-time-remaining" className="font-mono text-sm text-[#f8f7ff]">
                                {fmtClock(remaining)}
                            </span>
                            <span className="text-[#c77dff]/60">
                                · {mirror.state === "running" ? "LIVE" : (mirror.state || "idle").toUpperCase().replace("_", " ")}
                            </span>
                        </p>
                        <button
                            type="button"
                            onClick={() => setShowTopUp((s) => !s)}
                            data-testid="add-mirror-time-button"
                            className="btn-ghost-glow !px-4 !py-1.5 !text-[10px]"
                        >
                            ADD MIRROR TIME
                        </button>
                    </div>
                )}

                <div data-testid="chat-messages" className="flex-1 space-y-5 overflow-y-auto py-6">
                    {messages.map((m) =>
                        m.kind === "recognition" ? (
                            <RecognitionCard key={m.id} m={m} onContinue={() => setShowTopUp(true)} />
                        ) : (
                            <Bubble key={m.id} m={m} />
                        ),
                    )}

                    {gazing && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="typing-indicator" className="flex items-center gap-3 text-[#e0aaff]/70">
                            <span className="flex gap-1.5">
                                {[0, 1, 2].map((i) => (
                                    <span key={i} className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-[#c77dff]" style={{ animationDelay: `${i * 0.35}s` }} />
                                ))}
                            </span>
                            <span className="font-cormorant text-base italic">Mara is looking through the mirror…</span>
                        </motion.div>
                    )}

                    {promoAvailable && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="promo-banner"
                            className="rounded-xl border border-[#ffd700]/30 bg-[#ffd700]/5 p-4 text-center"
                        >
                            <p className="font-cinzel text-xs tracking-[0.25em] text-[#ffd700]/90">FREE FOR 24 HOURS</p>
                            <p className="mt-1 font-cormorant text-sm italic text-[#e0aaff]/80">
                                Every reading is free while the mirror stands open — five minutes each, no more.
                            </p>
                            <button
                                type="button"
                                onClick={claimPromo}
                                data-testid="promo-claim-button"
                                className="btn-ghost-glow mt-3 !border-[#ffd700]/50 !px-5 !py-2 !text-[10px] !text-[#ffd700]/90"
                            >
                                CLAIM 5 FREE MINUTES
                            </button>
                        </motion.div>
                    )}

                    {mirrorClosing && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="mirror-closing-banner"
                            className="rounded-xl border border-amber-300/30 bg-amber-300/5 p-4 text-center"
                        >
                            <p className="font-cinzel text-xs tracking-[0.25em] text-amber-200">THE MIRROR IS BEGINNING TO CLOSE</p>
                            <p className="mt-1 font-cormorant text-sm italic text-[#e0aaff]/80">
                                You have two minutes remaining with Mara.
                            </p>
                            <div className="mt-3 flex flex-wrap justify-center gap-2">
                                {TIME_TIERS.map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => requestTime(t)}
                                        data-testid={`quick-add-${t.minutes}`}
                                        className="btn-ghost-glow !px-4 !py-1.5 !text-[10px]"
                                    >
                                        REQUEST {t.minutes} MIN
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {mirrorClosed && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="mirror-closed-banner"
                            className="rounded-xl border border-[#c77dff]/30 bg-[#18002e]/60 p-5 text-center"
                        >
                            <p className="font-cinzel text-xs tracking-[0.25em] text-[#e0aaff]">THE MIRROR HAS CLOSED FOR NOW</p>
                            <p className="mt-2 font-cormorant text-sm italic leading-relaxed text-[#e0aaff]/75">
                                Your conversation has been preserved. Add more time whenever you are ready to return.
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowTopUp(true)}
                                data-testid="reopen-mirror-button"
                                className="btn-glow relative mt-4 !px-6 !py-3 !text-xs"
                            >
                                <span className="relative">REOPEN THE MIRROR</span>
                            </button>
                        </motion.div>
                    )}
                    {mirror.state === "ended" &&
                        (data?.rating ? (
                            <p data-testid="rating-thanks" className="rounded-xl border border-[#c77dff]/20 bg-[#18002e]/40 p-4 text-center font-cormorant text-sm italic text-[#e0aaff]/75">
                                You gave this crossing {"★".repeat(data.rating.stars)} — your words stay between you, Mara
                                and the mirror.
                            </p>
                        ) : (
                            <RatingPanel conversationId={conversationId} onDone={load} />
                        ))}
                    <div ref={bottomRef} />
                </div>

                <AnimatePresence>
                    {showTopUp && <RequestTimePanel balance={remaining} requesting={requesting} onRequest={requestTime} />}
                </AnimatePresence>

                {lockedInput ? (
                    <div className="border-t border-purple-500/20 py-4">
                        {blocked ? (
                            <div
                                data-testid="chat-blocked-bar"
                                className="w-full rounded-full border border-rose-300/30 bg-[#18002e]/70 px-5 py-3.5 text-center font-cinzel text-xs tracking-[0.2em] text-rose-200/80"
                            >
                                THE MIRROR IS CLOSED TO THIS PATH — MARA HAS SEALED THIS CROSSING
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowTopUp(true)}
                                data-testid="chat-locked-bar"
                                className="w-full rounded-full border border-[#c77dff]/40 bg-[#18002e]/70 px-5 py-3.5 font-cinzel text-xs tracking-[0.2em] text-[#e0aaff] transition-[box-shadow] duration-300 hover:shadow-[0_0_25px_rgba(199,125,255,0.35)]"
                            >
                                THE MIRROR NEEDS TIME — ADD MIRROR MINUTES TO CONTINUE
                            </button>
                        )}
                    </div>
                ) : (
                    <form onSubmit={send} className="border-t border-purple-500/20 py-4">
                        <div className="flex items-center gap-2.5">
                            <input
                                type="file"
                                accept="image/*"
                                ref={photoRef}
                                onChange={onPhoto}
                                data-testid="chat-photo-input"
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => photoRef.current?.click()}
                                disabled={hasPhoto}
                                title={hasPhoto ? "One photograph per crossing" : "Send one photograph"}
                                data-testid="chat-attach-photo"
                                aria-label="Send one photograph"
                                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-purple-500/30 text-[#e0aaff]/70 transition-[color,border-color,box-shadow] duration-300 hover:border-[#c77dff]/60 hover:text-[#f8f7ff] disabled:opacity-40"
                            >
                                <ImagePlus className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={toggleRecording}
                                disabled={hasVoice && !recording}
                                title={hasVoice ? "One voice note per crossing" : recording ? "Stop and send your whisper" : "Send one voice note"}
                                data-testid="chat-voice-record-button"
                                aria-label="Send one voice note"
                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition-[color,border-color,box-shadow] duration-300 disabled:opacity-40 ${
                                    recording
                                        ? "border-rose-300/60 text-rose-200 shadow-[0_0_20px_rgba(253,164,175,0.35)]"
                                        : "border-purple-500/30 text-[#e0aaff]/70 hover:border-[#c77dff]/60 hover:text-[#f8f7ff]"
                                }`}
                            >
                                {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                            </button>
                            <input
                                type="text"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                data-testid="chat-input"
                                placeholder="Speak into the mirror…"
                                className="w-full rounded-full border border-purple-500/30 bg-[#0b031e]/70 px-5 py-3.5 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 transition-[border-color,box-shadow] duration-300 focus:border-[#c77dff]/70 focus:shadow-[0_0_20px_rgba(199,125,255,0.25)] focus:outline-none"
                            />
                            <button
                                type="submit"
                                disabled={sending || !text.trim()}
                                data-testid="chat-send-button"
                                aria-label="Send message"
                                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#c77dff]/50 bg-[#5a189a]/60 text-[#f8f7ff] shadow-[0_0_20px_rgba(123,44,191,0.45)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_0_30px_rgba(199,125,255,0.6)] active:scale-95 disabled:opacity-50"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </div>
                        {recording && (
                            <p className="mt-2 text-center text-[11px] tracking-wider text-[#c77dff]/70">
                                The mirror is listening… press again to send your whisper.
                            </p>
                        )}
                    </form>
                )}
            </div>

            <AnimatePresence>
                {flash && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6 }}
                        data-testid="mirror-flash"
                        className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center"
                    >
                        <motion.div
                            initial={{ scale: 0, opacity: 0.9 }}
                            animate={{ scale: 2.4, opacity: 0 }}
                            transition={{ duration: 2.2, ease: "easeOut" }}
                            className="absolute h-[40rem] w-[40rem] rounded-full bg-[radial-gradient(circle,rgba(224,170,255,0.5)_0%,rgba(123,44,191,0.35)_40%,transparent_70%)]"
                        />
                        <motion.p
                            initial={{ opacity: 0, y: 20, letterSpacing: "0.2em" }}
                            animate={{ opacity: 1, y: 0, letterSpacing: "0.45em" }}
                            transition={{ duration: 1.4 }}
                            className="text-glow-pearl relative font-display text-xl font-bold text-[#f8f7ff] sm:text-3xl"
                        >
                            THE MIRROR RECOGNISES HER
                        </motion.p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};
